interface UserNameParts {
  givenName?: string | null;
  familyName?: string | null;
}

/**
 * `order: "auto"` (the default) renders family-first for Hangul names so a
 * Korean driver's name reads correctly regardless of the viewer's locale;
 * everything else is given-first.
 */
export type UserNameOrder = "auto" | "given-first" | "family-first";

export interface FormatUserNameOptions {
  order?: UserNameOrder;
}

const HANGUL_RE = /[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\uD7B0-\uD7FF]/;

function containsHangul(value: string): boolean {
  return HANGUL_RE.test(value);
}

/**
 * Display name for a user, trimmed so a missing half leaves no stray
 * whitespace. Returns `""` when both halves are blank so callers can decide
 * on their own fallback.
 *
 * Ordering: `"auto"` (default) is script-based — Hangul names render
 * family-first with no inner space (`박민지`); other scripts default to
 * `"given-first"` (`"Ada Lovelace"`). Pass an explicit order to override.
 */
export function formatUserName(
  user: UserNameParts,
  options: FormatUserNameOptions = {},
): string {
  const given = (user.givenName ?? "").trim();
  const family = (user.familyName ?? "").trim();
  const order = options.order ?? "auto";
  const familyFirst =
    order === "family-first" ||
    (order === "auto" && containsHangul(given) && containsHangul(family));
  if (!familyFirst) {
    return `${given} ${family}`.trim();
  }
  const separator = containsHangul(given) && containsHangul(family) ? "" : " ";
  return `${family}${separator}${given}`.trim();
}
